import { type ReactNode } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";

/**
 * The chrome both guided runs share: the demo guide's rail for logged-out
 * visitors, and the onboarding rail for trialists.
 *
 * Extracted so the two read as one system rather than two that resemble each
 * other. Everything here was learned once and should not have to be learned
 * again on the other surface:
 *
 * - Sized to be read. The first version was a 320px box in the bottom corner of
 *   a 2400px screen, which on a monitor that size is indistinguishable from a
 *   cookie notice — it was ignored because it looked like something to dismiss.
 *   This is roughly twice the width with its heading at body size.
 * - Blue, not grey. A gray-900 panel with a gray-700 border is the same two
 *   colours as every widget behind it, so it read as another panel rather than
 *   as the app talking. A blue rim alone did not fix that; the wash carries the
 *   blue across the whole surface.
 * - z-40, BELOW every dialog. At z-[90] the demo's rail outranked the editors it
 *   sends people into (those run z-50 to z-[70]) and covered the Save button of
 *   the thing it was asking for. While a dialog is open, the dialog is the
 *   instruction.
 * - Never anchored to an element. The dashboard drags, resizes and reflows
 *   across five breakpoints, so a measured position would be stale the moment
 *   anything moved. What it points at is ringed or pulsed instead.
 *
 * Deliberately NOT included: the copy, the corner choice, the progress
 * semantics or the footer. Those differ per surface and per step, and are the
 * caller's business.
 */
export default function RailShell({
  eyebrow,
  stepIndex,
  stepTotal,
  showProgress = true,
  corner,
  onClose,
  closeLabel,
  closeAnalyticsId,
  children,
  footer,
}: {
  /** Small uppercase label above the counter. */
  eyebrow: string;
  /** Zero-based index of the live step. Ignored when showProgress is false. */
  stepIndex: number;
  stepTotal: number;
  /** False once the run is complete — there is no step to count. */
  showProgress?: boolean;
  /** Tailwind position classes. Surfaces move between pages; see callers. */
  corner: string;
  onClose: () => void;
  closeLabel: string;
  closeAnalyticsId: string;
  /** The step body. Give it a `key` so it re-enters on each step change. */
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div
      className={clsx(
        "no-drag fixed z-40 hidden w-[26rem] transition-[top,bottom] duration-300 sm:block lg:w-[30rem] short-screen:w-[24rem]",
        corner
      )}
    >
      {/* Capped against the viewport: the min-height below is a floor, not a
          promise, and on a short screen a 30rem panel would otherwise run off
          the bottom of the window. Depth (drop shadow, blurred backdrop) does
          the separating, so the dashboard stays visible and usable underneath —
          which a scrim would have cost. */}
      <div className="flex max-h-[calc(100vh-5.5rem)] flex-col overflow-hidden rounded-xl border border-squawk-blue/70 bg-gradient-to-b from-squawk-blue/[0.12] to-gray-900/95 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.9)] ring-1 ring-squawk-blue/30 backdrop-blur-md">
        {/* Accent edge — the panel has to read as the app talking, not as an
            ad sitting on top of it. */}
        <div className="h-1.5 w-full bg-squawk-blue" />

        {/* The min-height is a floor, not a promise — it stops a short step
            from collapsing into a tooltip, and the max-height above stops a
            long one running off a short screen. Values carried over from the
            demo rail, where they were tuned against its end-of-run CTA (the
            tallest thing either surface renders). */}
        <div className="flex min-h-[22rem] flex-col overflow-y-auto p-6 scrollbar-thin lg:min-h-[26rem] lg:p-7 short-screen:min-h-0 short-screen:p-5">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-squawk-blue">
                {eyebrow}
              </p>
              {showProgress && (
                <p className="mt-1 text-sm text-gray-500">
                  Step {Math.min(stepIndex + 1, stepTotal)} of {stepTotal}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              data-analytics-id={closeAnalyticsId}
              className="-m-1 rounded p-1 text-gray-500 transition-colors hover:text-gray-300"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="mb-6 flex items-center gap-1.5 short-screen:mb-4" aria-hidden>
            {Array.from({ length: stepTotal }, (_, i) => (
              <span
                key={i}
                className={clsx(
                  "h-1 flex-1 rounded-full transition-colors duration-500",
                  // Reached, including the one in play, reads as solid blue;
                  // what is left reads as grey. An earlier version rendered
                  // finished steps at 40% against a solid current step, which
                  // made the work already done look fainter than the work in
                  // hand — a progress bar that dims behind you.
                  !showProgress || i <= stepIndex
                    ? "bg-squawk-blue"
                    : "bg-gray-800"
                )}
              />
            ))}
          </div>

          {children}

          {footer && (
            <div className="mt-6 flex items-center justify-between gap-3 border-t border-gray-800 pt-4">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
